import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Button from "../common/Button";
import Input from "../common/Input";
import { taskService } from "../../services/tasks";
import { teamService } from "../../services/team";
import { leadService } from "../../services/leads";
import { clientService } from "../../services/clients";
import toast from "react-hot-toast";

const TaskForm = ({ task, onSuccess }) => {
  const queryClient = useQueryClient();
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: task || {},
  });
  const [loading, setLoading] = useState(false);

  const recurring = watch("recurring");

  const { data: teamMembers } = useQuery({
    queryKey: ["team"],
    queryFn: teamService.getAll,
  });

  const { data: leads } = useQuery({
    queryKey: ["leads"],
    queryFn: () => leadService.getAll(),
  });

  const { data: clients } = useQuery({
    queryKey: ["clients"],
    queryFn: clientService.getAll,
  });

  useEffect(() => {
    reset(task || {});
  }, [task, reset]);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      if (task) {
        await taskService.update(task._id, data);
        toast.success("Task updated");
      } else {
        await taskService.create(data);
        toast.success("Task created");
      }
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      reset();
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.message || "Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <Input
        label="Title"
        {...register("title", { required: "Title is required" })}
        error={errors.title?.message}
      />
      <Input label="Description" {...register("description")} />
      <Input
        label="Due Date"
        type="date"
        {...register("dueDate", { required: "Due date is required" })}
        error={errors.dueDate?.message}
      />
      <div>
        <label className="block text-sm font-medium mb-1">Priority</label>
        <select {...register("priority")} className="w-full border rounded p-2">
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Assigned To</label>
        <select
          {...register("assignedTo")}
          className="w-full border rounded p-2"
        >
          <option value="">Select Team Member</option>
          {teamMembers?.map((member) => (
            <option key={member._id} value={member._id}>
              {member.name}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Related Lead</label>
        <select
          {...register("relatedLead")}
          className="w-full border rounded p-2"
        >
          <option value="">Select Lead</option>
          {leads?.map((lead) => (
            <option key={lead._id} value={lead._id}>
              {lead.name}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Related Client</label>
        <select
          {...register("relatedClient")}
          className="w-full border rounded p-2"
        >
          <option value="">Select Client</option>
          {clients?.map((client) => (
            <option key={client._id} value={client._id}>
              {client.name}
            </option>
          ))}
        </select>
      </div>
      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          id="recurring"
          {...register("recurring")}
          className="rounded"
        />
        <label htmlFor="recurring" className="text-sm font-medium">
          Recurring
        </label>
      </div>
      {recurring && (
        <div>
          <label className="block text-sm font-medium mb-1">
            Recurrence Pattern
          </label>
          <select
            {...register("recurrencePattern")}
            className="w-full border rounded p-2"
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
      )}
      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          id="completed"
          {...register("completed")}
          className="rounded"
        />
        <label htmlFor="completed" className="text-sm font-medium">
          Completed
        </label>
      </div>
      <Button type="submit" disabled={loading}>
        {loading ? "Saving..." : task ? "Update" : "Create"}
      </Button>
    </form>
  );
};

export default TaskForm;
